import React from "react";
import { Helmet } from "react-helmet-async";

interface SEOHelmetProps {
  title: string;
  description: string;
  keywords?: string;
  canonicalUrl?: string;
  ogImage?: string;
  ogType?: string;
  twitterCard?: string;
  author?: string;
  publishedTime?: string;
  modifiedTime?: string;
  articleSection?: string;
  articleTags?: string[];
}

const SEOHelmet: React.FC<SEOHelmetProps> = ({
  title,
  description,
  keywords,
  canonicalUrl,
  ogImage = "/images/og-default.jpg",
  ogType = "website",
  twitterCard = "summary_large_image",
  author,
  publishedTime,
  modifiedTime,
  articleSection,
  articleTags,
}) => {
  const siteName = "LicoMeds";
  const siteUrl = "https://licomeds.com";
  const imageUrl = ogImage.startsWith("http")
    ? ogImage
    : `${siteUrl}${ogImage}`;

  return (
    <Helmet>
      {/* Basic Meta Tags */}
      <title>{title}</title>
      <meta name="description" content={description} />
      {keywords && <meta name="keywords" content={keywords} />}
      {author && <meta name="author" content={author} />}

      {/* Canonical URL */}
      {canonicalUrl && <link rel="canonical" href={canonicalUrl} />}

      {/* Open Graph Tags */}
      <meta property="og:type" content={ogType} />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:image" content={imageUrl} />
      <meta property="og:site_name" content={siteName} />
      {canonicalUrl && <meta property="og:url" content={canonicalUrl} />}

      {/* Article specific Open Graph tags */}
      {ogType === "article" && (
        <>
          {author && <meta property="article:author" content={author} />}
          {publishedTime && (
            <meta property="article:published_time" content={publishedTime} />
          )}
          {modifiedTime && (
            <meta property="article:modified_time" content={modifiedTime} />
          )}
          {articleSection && (
            <meta property="article:section" content={articleSection} />
          )}
          {articleTags &&
            articleTags.map((tag, index) => (
              <meta key={index} property="article:tag" content={tag} />
            ))}
        </>
      )}

      {/* Twitter Card Tags */}
      <meta name="twitter:card" content={twitterCard} />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={imageUrl} />
      <meta name="twitter:site" content="@licomeds" />
      <meta name="twitter:creator" content="@licomeds" />

      {/* Additional Meta Tags */}
      <meta name="robots" content="index, follow" />
      <meta name="googlebot" content="index, follow" />
      <meta name="language" content="en-NG" />
      <meta name="geo.region" content="NG" />
      <meta name="geo.country" content="Nigeria" />
      <meta name="geo.placename" content="Nigeria" />

      {/* Mobile and App Tags */}
      <meta name="viewport" content="width=device-width, initial-scale=1.0" />
      <meta name="format-detection" content="telephone=no" />
      <meta name="mobile-web-app-capable" content="yes" />
      <meta name="apple-mobile-web-app-capable" content="yes" />
      <meta name="apple-mobile-web-app-status-bar-style" content="default" />

      {/* Healthcare and Nigeria specific tags */}
      <meta name="category" content="Healthcare" />
      <meta name="coverage" content="Nigeria" />
      <meta name="distribution" content="Global" />
      <meta name="rating" content="General" />

      {/* Structured Data for Healthcare */}
      <script type="application/ld+json">
        {JSON.stringify({
          "@context": "https://schema.org",
          "@type": ogType === "article" ? "MedicalWebPage" : "WebPage",
          name: title,
          description: description,
          url: canonicalUrl,
          publisher: {
            "@type": "Organization",
            name: siteName,
            url: siteUrl,
            logo: {
              "@type": "ImageObject",
              url: `${siteUrl}/logo.png`,
            },
          },
          inLanguage: "en-NG",
          about: {
            "@type": "MedicalCondition",
            name: "Healthcare Services Nigeria",
          },
          audience: {
            "@type": "PeopleAudience",
            geographicArea: {
              "@type": "Country",
              name: "Nigeria",
            },
          },
          ...(ogType === "article" &&
            author && {
              author: {
                "@type": "Person",
                name: author,
              },
              datePublished: publishedTime,
              dateModified: modifiedTime || publishedTime,
              mainEntityOfPage: {
                "@type": "WebPage",
                "@id": canonicalUrl,
              },
            }),
        })}
      </script>

      {/* Organization identity: office NAP + parent company + sibling brands (site-wide) */}
      <script type="application/ld+json">
        {JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Organization",
          name: siteName,
          url: siteUrl,
          logo: `${siteUrl}/logo.png`,
          address: {
            "@type": "PostalAddress",
            streetAddress: "No. 75 Chime Avenue",
            addressLocality: "New Haven, Enugu",
            addressRegion: "Enugu",
            addressCountry: "NG",
          },
          contactPoint: {
            "@type": "ContactPoint",
            telephone: "+2349078801125",
            contactType: "customer support",
            areaServed: "NG",
          },
          parentOrganization: {
            "@type": "Organization",
            name: "Learn and Innovate Technologies Limited",
            url: "https://learnandinnovatespace.com",
          },
          sameAs: [
            "https://learnandinnovatespace.com",
            "https://licoleverage.ng",
          ],
        })}
      </script>
    </Helmet>
  );
};

export default SEOHelmet;
